import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { appointmentsAPI, filesAPI, balanceAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  FileText, 
  CreditCard, 
  User,
  ArrowRight,
  Clock,
  Euro,
  Download,
  Plus,
  TrendingUp,
  TrendingDown,
  Tag,
  Percent,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  
  const [appointments, setAppointments] = useState([]);
  const [files, setFiles] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [myDiscounts, setMyDiscounts] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aptRes, filesRes, historyRes, balanceRes] = await Promise.all([
          appointmentsAPI.getAll(),
          filesAPI.getAll(),
          balanceAPI.getHistory(),
          balanceAPI.get(),
        ]);
        
        setAppointments(aptRes.data);
        setFiles(filesRes.data);
        setBalanceHistory(historyRes.data);
        setBalance(balanceRes.data.balance);
        
        // Load customer-specific data
        try {
          const [discRes, docRes] = await Promise.all([
            api.get('/customer/my-discounts'),
            api.get('/customer/my-documents')
          ]);
          setMyDiscounts(discRes.data || []);
          setMyDocuments(docRes.data || []);
        } catch (e) {
          // Non-critical - may fail for some users
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // If we're on a sub-route, render the Outlet
  if (location.pathname !== '/dashboard') {
    return <Outlet />;
  }

  const upcomingAppointments = appointments
    .filter(apt => apt.status !== 'cancelled' && new Date(apt.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 3);

  const recentFiles = files.slice(0, 3);
  const recentBalanceHistory = balanceHistory.slice(0, 5);

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      completed: 'Abgeschlossen',
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{labels[status] || status}</Badge>;
  };

  const getBalanceColor = () => {
    if (balance < 0) return 'text-red-600';
    if (balance > 0) return 'text-emerald-600';
    return 'text-gray-900';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 sm:py-8" data-testid="customer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Welcome Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-barlow font-bold text-2xl sm:text-3xl text-[#1A1A1A] mb-1 sm:mb-2">
            Willkommen, {user?.first_name}!
          </h1>
          <p className="font-manrope text-sm sm:text-base text-gray-600">
            Kundennummer: <span className="font-medium text-[#9B1B30]">{user?.customer_number}</span>
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="card-hover" data-testid="stat-appointments">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Termine</p>
                  <p className="font-barlow font-bold text-xl sm:text-2xl text-[#1A1A1A]">
                    {appointments.filter(a => a.status !== 'cancelled').length}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#9B1B30]/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#9B1B30]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-documents">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Dokumente</p>
                  <p className="font-barlow font-bold text-xl sm:text-2xl text-[#1A1A1A]">{files.length}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2E2A88]/10 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#2E2A88]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-payments">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Zahlungen</p>
                  <p className="font-barlow font-bold text-xl sm:text-2xl text-[#1A1A1A]">{balanceHistory.length}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-balance">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Saldo</p>
                  <p className={`font-barlow font-bold text-xl sm:text-2xl ${getBalanceColor()}`}>
                    {balance.toFixed(2).replace('.', ',')} €
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Euro className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Upcoming Appointments */}
          <Card data-testid="upcoming-appointments">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-barlow text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#9B1B30]" />
                Kommende Termine
              </CardTitle>
              <Link to="/dashboard/termine">
                <Button variant="ghost" size="sm" className="text-[#9B1B30]">
                  Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {upcomingAppointments.length > 0 ? (
                <div className="space-y-4">
                  {upcomingAppointments.map((apt) => (
                    <div 
                      key={apt.id} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#9B1B30] text-white rounded-lg flex flex-col items-center justify-center">
                          <span className="font-barlow font-bold text-lg leading-none">
                            {format(new Date(apt.date), 'd')}
                          </span>
                          <span className="font-manrope text-xs">
                            {format(new Date(apt.date), 'MMM', { locale: de })}
                          </span>
                        </div>
                        <div>
                          <p className="font-manrope font-medium">
                            {format(new Date(apt.date), 'EEEE', { locale: de })}
                          </p>
                          <p className="font-manrope text-sm text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {apt.time} Uhr · {apt.duration} Min
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(apt.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500 mb-4">Keine kommenden Termine</p>
                  <Link to="/termin">
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" /> Termin buchen
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Documents */}
          <Card data-testid="recent-documents">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-barlow text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#2E2A88]" />
                Dokumente
              </CardTitle>
              <Link to="/dashboard/dokumente">
                <Button variant="ghost" size="sm" className="text-[#9B1B30]">
                  Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentFiles.length > 0 ? (
                <div className="space-y-3">
                  {recentFiles.map((file) => (
                    <div 
                      key={file.id} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#2E2A88]/10 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#2E2A88]" />
                        </div>
                        <div>
                          <p className="font-manrope font-medium text-sm truncate max-w-[200px]">
                            {file.original_name}
                          </p>
                          <p className="font-manrope text-xs text-gray-500">
                            {format(new Date(file.uploaded_at), 'dd.MM.yyyy')}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500">Keine Dokumente vorhanden</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Balance History */}
          <Card className="lg:col-span-2" data-testid="recent-payments">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-barlow text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Zahlungshistorie
              </CardTitle>
              <Link to="/dashboard/zahlungen">
                <Button variant="ghost" size="sm" className="text-[#9B1B30]">
                  Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentBalanceHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 font-barlow font-bold text-sm text-gray-500">Datum & Uhrzeit</th>
                        <th className="text-left py-3 font-barlow font-bold text-sm text-gray-500">Beschreibung</th>
                        <th className="text-right py-3 font-barlow font-bold text-sm text-gray-500">Betrag</th>
                        <th className="text-right py-3 font-barlow font-bold text-sm text-gray-500">Neuer Stand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBalanceHistory.map((entry) => (
                        <tr key={entry.id} className="border-b last:border-0 table-row-hover">
                          <td className="py-4 font-manrope text-sm">
                            {format(new Date(entry.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                          </td>
                          <td className="py-4 font-manrope text-sm">{entry.description}</td>
                          <td className={`py-4 font-barlow font-bold text-right ${
                            entry.amount < 0 ? 'text-red-600' : 'text-emerald-600'
                          }`}>
                            <div className="flex items-center justify-end gap-1">
                              {entry.amount < 0 ? (
                                <TrendingDown className="w-4 h-4" />
                              ) : (
                                <TrendingUp className="w-4 h-4" />
                              )}
                              {entry.amount > 0 ? '+' : ''}{entry.amount.toFixed(2).replace('.', ',')} €
                            </div>
                          </td>
                          <td className="py-4 font-barlow font-bold text-right text-gray-600">
                            {entry.new_balance.toFixed(2).replace('.', ',')} €
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500">Keine Zahlungen vorhanden</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Discounts & Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 mt-6">
          {/* My Discounts */}
          <Card data-testid="my-discounts">
            <CardHeader>
              <CardTitle className="font-barlow text-xl flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#9B1B30]" />
                Meine Rabatte
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myDiscounts.length > 0 ? (
                <div className="space-y-3">
                  {myDiscounts.map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${d.type === 'percentage' ? 'bg-purple-100' : 'bg-emerald-100'}`}>
                          {d.type === 'percentage' ? <Percent className="w-4 h-4 text-purple-600" /> : <Euro className="w-4 h-4 text-emerald-600" />}
                        </div>
                        <div>
                          <p className="font-mono font-bold text-sm">{d.code}</p>
                          <p className="text-xs text-gray-500">{d.name || 'Rabatt'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-[#9B1B30]">
                          {d.type === 'percentage' ? `${d.value}%` : `${d.value.toFixed(2)} EUR`}
                        </span>
                        <Button variant="ghost" size="sm" className="ml-1 h-7 w-7 p-0" onClick={() => { navigator.clipboard.writeText(d.code); toast.success('Code kopiert'); }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500">Keine Rabatte verfügbar</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Documents (Invoices/Quotes) */}
          <Card data-testid="my-documents">
            <CardHeader>
              <CardTitle className="font-barlow text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#9B1B30]" />
                Meine Rechnungen & Angebote
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myDocuments.length > 0 ? (
                <div className="space-y-3">
                  {myDocuments.slice(0, 5).map((doc) => {
                    const typeLabels = { rechnung: 'Rechnung', angebot: 'Angebot', auftragsbestaetigung: 'Auftragsbestätigung' };
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{typeLabels[doc.document_type] || doc.document_type}</Badge>
                            <span className="font-mono text-sm font-semibold">{doc.document_number}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.document_date} — {doc.gross_total ? `${doc.gross_total.toFixed(2).replace('.', ',')} EUR` : ''}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await api.get(`/customer/my-documents/${doc.id}/pdf`, { responseType: 'blob' });
                              const url = URL.createObjectURL(new Blob([res.data]));
                              const a = document.createElement('a');
                              a.href = url; a.download = `${doc.document_number}.pdf`; a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) { toast.error('Download fehlgeschlagen'); }
                          }}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500">Keine Dokumente vorhanden</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Account Info */}
        <Card className="mt-8" data-testid="account-info">
          <CardHeader>
            <CardTitle className="font-barlow text-xl flex items-center gap-2">
              <User className="w-5 h-5 text-[#9B1B30]" />
              Ihre Kontodaten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Name</p>
                <p className="font-manrope font-medium">{user?.first_name} {user?.last_name}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">E-Mail</p>
                <p className="font-manrope font-medium">{user?.email}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Telefon</p>
                <p className="font-manrope font-medium">{user?.phone}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Adresse</p>
                <p className="font-manrope font-medium">{user?.street}</p>
                <p className="font-manrope font-medium">{user?.postal_code} {user?.city}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Kundennummer</p>
                <p className="font-barlow font-bold text-[#9B1B30]">{user?.customer_number}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Kontostand</p>
                <p className={`font-barlow font-bold text-xl ${getBalanceColor()}`}>
                  {balance.toFixed(2).replace('.', ',')} €
                </p>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/einstellungen">
                <Button variant="outline">
                  Einstellungen bearbeiten
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
